import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiChevronDown } from 'react-icons/fi'
import {
  updateShipment,
  INTEGRATED_SHIPPING_LINE_OPTIONS,
  SHIPMENT_STATUS_OPTIONS,
  populateFromContracts,
  type ShipmentDisplay,
} from '@/services/api/shipments'
import { getContracts, type Contract } from '@/services/api/contracts'
import ProductSelectInput from '@/components/products/ProductSelectInput'
import CustomerSelectInput from '@/components/customers/CustomerSelectInput'
import StockReferenceSelectInput from '@/components/inventory/StockReferenceSelectInput'

interface EditShipmentModalProps {
  isOpen: boolean
  onClose: () => void
  shipmentData?: ShipmentDisplay
  onSuccess?: () => void
}

const emptyIfDash = (v: string | undefined) => (v && v !== '—' ? v : '')

const labelStyle = {
  fontFamily: 'Lexend Deca',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '22px',
  color: '#69697A',
} as const

const inputStyle = (hasValue: boolean) => ({
  fontFamily: 'Lexend Deca',
  fontWeight: hasValue ? 500 : 300,
  fontSize: '12px',
  lineHeight: '20px',
  height: '40px',
  color: hasValue ? '#212126' : '#69697A',
})

const EditShipmentModal = ({ isOpen, onClose, shipmentData, onSuccess }: EditShipmentModalProps) => {
  const [formData, setFormData] = useState({
    buyContractId: '',
    saleContractId: '',
    buyContract: '',
    saleContract: '',
    booking: '',
    billOfLading: '',
    containerNumber: '',
    vessel: '',
    shippingLine: '',
    product: '',
    customer: '',
    factory: '',
    origin: '',
    destination: '',
    etd: '',
    eta: '',
    status: 'pending',
  })
  const [buyContracts, setBuyContracts] = useState<Contract[]>([])
  const [saleContracts, setSaleContracts] = useState<Contract[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    getContracts({ type: 'buy', limit: 100 }).then((r) => setBuyContracts(r.contracts)).catch(() => {})
    getContracts({ type: 'sale', limit: 100 }).then((r) => setSaleContracts(r.contracts)).catch(() => {})
  }, [isOpen])

  useEffect(() => {
    if (!shipmentData || !isOpen) return

    const buyMatch = buyContracts.find((c) => c.contractNumber === shipmentData.buyContract)
    const saleMatch = saleContracts.find((c) => c.contractNumber === shipmentData.saleContract)

    setFormData({
      buyContractId: buyMatch?.id ?? '',
      saleContractId: saleMatch?.id ?? '',
      buyContract: emptyIfDash(shipmentData.buyContract),
      saleContract: emptyIfDash(shipmentData.saleContract),
      booking: emptyIfDash(shipmentData.booking),
      billOfLading: emptyIfDash(shipmentData.billOfLading),
      containerNumber: emptyIfDash(shipmentData.containerNumber),
      vessel: emptyIfDash(shipmentData.vessel),
      shippingLine: emptyIfDash(shipmentData.shippingLine),
      product: emptyIfDash(shipmentData.product),
      customer: emptyIfDash(shipmentData.customer),
      factory: emptyIfDash(shipmentData.factory),
      origin: emptyIfDash(shipmentData.origin),
      destination: emptyIfDash(shipmentData.destination),
      etd: shipmentData.etdInput || '',
      eta: shipmentData.etaInput || '',
      status: shipmentData.status || 'pending',
    })
    setSubmitError(null)
  }, [shipmentData, isOpen, buyContracts, saleContracts])

  const handleContractChange = async (field: 'buyContractId' | 'saleContractId', value: string) => {
    const contract =
      field === 'buyContractId'
        ? buyContracts.find((c) => c.id === value)
        : saleContracts.find((c) => c.id === value)

    const updated = {
      ...formData,
      [field]: value,
      buyContract: field === 'buyContractId' ? (contract?.contractNumber ?? '') : formData.buyContract,
      saleContract: field === 'saleContractId' ? (contract?.contractNumber ?? '') : formData.saleContract,
    }
    setFormData(updated)
    setSubmitError(null)

    if (!updated.buyContractId && !updated.saleContractId) return
    try {
      const populated = await populateFromContracts({
        buyContractId: updated.buyContractId || undefined,
        saleContractId: updated.saleContractId || undefined,
      })
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        buyContract: populated.buyContract || prev.buyContract,
        saleContract: populated.saleContract || prev.saleContract,
        customer: populated.customer || prev.customer,
        factory: populated.factory || prev.factory,
        origin: populated.pol || prev.origin,
        destination: populated.pod || prev.destination,
        booking: populated.booking || prev.booking,
      }))
    } catch {
      // Non-critical
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setSubmitError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!shipmentData?.id) {
      setSubmitError('Shipment ID is missing.')
      return
    }

    if (formData.etd && formData.eta && formData.eta < formData.etd) {
      setSubmitError('ETA cannot be earlier than ETD.')
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      const val = (s: string) => (s.trim() && s.trim() !== '—' ? s.trim() : undefined)
      await updateShipment(shipmentData.id, {
        buyContract: val(formData.buyContract),
        saleContract: val(formData.saleContract),
        booking: val(formData.booking),
        bl: val(formData.billOfLading),
        containerNumber: val(formData.containerNumber),
        vessel: val(formData.vessel),
        carrier: val(formData.shippingLine),
        product: val(formData.product),
        customer: val(formData.customer),
        factory: val(formData.factory),
        pol: val(formData.origin),
        pod: val(formData.destination),
        etd: formData.etd || undefined,
        eta: formData.eta || undefined,
        status: formData.status || undefined,
      })
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response?.data
      const msg = res?.error?.message ?? res?.message ?? (err as Error)?.message ?? 'Failed to update shipment.'
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '548px',
          maxWidth: '548px',
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{ width: '100%', height: '74px', backgroundColor: '#F2F4F2' }}
        >
          <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', color: '#212126' }}>
            Edit Shipment
          </h2>
          <button onClick={onClose} className="flex items-center justify-center hover:opacity-80" style={{ width: 24, height: 24 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form
          id="edit-shipment-form"
          onSubmit={handleSubmit}
          className="p-4 sm:p-6 flex-1 scrollbar-hide"
          style={{ overflowY: 'auto', minHeight: 0 }}
        >
          {submitError && (
            <div className="mb-4 px-3 py-2 rounded" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', fontSize: '12px' }}>
              {submitError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Buy Contract */}
            <div>
              <label className="block mb-2" style={labelStyle}>Buy Contract</label>
              <div className="relative">
                <select
                  name="buyContractId"
                  value={formData.buyContractId}
                  onChange={(e) => handleContractChange('buyContractId', e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ ...inputStyle(!!formData.buyContractId), backgroundColor: '#FFFFFF' }}
                >
                  <option value="">Select buy contract</option>
                  {buyContracts.map((c) => (
                    <option key={c.id} value={c.id}>{c.contractNumber} — {c.title}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ width: 16, height: 16, color: '#69697A' }} />
              </div>
            </div>

            {/* Sale Contract */}
            <div>
              <label className="block mb-2" style={labelStyle}>Sale Contract</label>
              <div className="relative">
                <select
                  name="saleContractId"
                  value={formData.saleContractId}
                  onChange={(e) => handleContractChange('saleContractId', e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ ...inputStyle(!!formData.saleContractId), backgroundColor: '#FFFFFF' }}
                >
                  <option value="">Select sale contract</option>
                  {saleContracts.map((c) => (
                    <option key={c.id} value={c.id}>{c.contractNumber} — {c.title}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ width: 16, height: 16, color: '#69697A' }} />
              </div>
            </div>

            {/* Booking */}
            <div>
              <label className="block mb-2" style={labelStyle}>Booking</label>
              <StockReferenceSelectInput
                type="booking"
                value={formData.booking}
                onChange={(booking) => setFormData((prev) => ({ ...prev, booking }))}
              />
            </div>

            {/* Bill of Lading */}
            <div>
              <label className="block mb-2" style={labelStyle}>Bill of Lading</label>
              <input type="text" name="billOfLading" value={formData.billOfLading} onChange={handleChange} placeholder="Enter bill of lading number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.billOfLading)} />
            </div>

            {/* Container Number */}
            <div className="sm:col-span-2">
              <label className="block mb-2" style={labelStyle}>Container Number</label>
              <input type="text" name="containerNumber" value={formData.containerNumber} onChange={handleChange} placeholder="Enter container number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.containerNumber)} />
            </div>

            {/* Vessel */}
            <div>
              <label className="block mb-2" style={labelStyle}>Vessel</label>
              <input type="text" name="vessel" value={formData.vessel} onChange={handleChange} placeholder="Enter vessel name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.vessel)} />
            </div>

            {/* Shipping Line */}
            <div>
              <label className="block mb-2" style={labelStyle}>Shipping Line</label>
              <div className="relative">
                <select
                  name="shippingLine"
                  value={formData.shippingLine}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ ...inputStyle(!!formData.shippingLine), backgroundColor: '#FFFFFF' }}
                >
                  <option value="">Select shipping line</option>
                  {INTEGRATED_SHIPPING_LINE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                  {(() => {
                    const raw = formData.shippingLine.trim()
                    if (!raw) return null
                    const norm = (s: string) => s.toLowerCase().replace(/\s+/g, '-')
                    const known = INTEGRATED_SHIPPING_LINE_OPTIONS.some((o) => norm(o.value) === norm(raw))
                    if (known) return null
                    return <option value={raw}>{raw} (existing)</option>
                  })()}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Product */}
            <div>
              <label className="block mb-2" style={labelStyle}>Product</label>
              <ProductSelectInput value={formData.product} onChange={(product) => setFormData((prev) => ({ ...prev, product }))} />
            </div>

            {/* Customer */}
            <div>
              <label className="block mb-2" style={labelStyle}>Customer</label>
              <CustomerSelectInput
                value={formData.customer}
                onChange={(customer) => setFormData((prev) => ({ ...prev, customer }))}
              />
            </div>

            {/* Factory */}
            <div>
              <label className="block mb-2" style={labelStyle}>Factory</label>
              <input type="text" name="factory" value={formData.factory} onChange={handleChange} placeholder="Enter factory name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.factory)} />
            </div>

            {/* Origin */}
            <div>
              <label className="block mb-2" style={labelStyle}>Origin</label>
              <input type="text" name="origin" value={formData.origin} onChange={handleChange} placeholder="Enter origin" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.origin)} />
            </div>

            {/* Destination */}
            <div>
              <label className="block mb-2" style={labelStyle}>Destination</label>
              <input type="text" name="destination" value={formData.destination} onChange={handleChange} placeholder="Enter destination" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.destination)} />
            </div>

            {/* ETD */}
            <div>
              <label className="block mb-2" style={labelStyle}>ETD (departure)</label>
              <input type="date" name="etd" value={formData.etd} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.etd)} />
            </div>

            {/* ETA */}
            <div>
              <label className="block mb-2" style={labelStyle}>ETA</label>
              <input type="date" name="eta" value={formData.eta} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" style={inputStyle(!!formData.eta)} />
            </div>

            {/* Status */}
            <div className="sm:col-span-2">
              <label className="block mb-2" style={labelStyle}>Status</label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ ...inputStyle(!!formData.status), backgroundColor: '#FFFFFF' }}
                >
                  {SHIPMENT_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
        </form>

        <div
          className="flex items-center justify-end gap-4 border-t border-gray-200 flex-shrink-0"
          style={{ height: '74px', backgroundColor: '#F2F4F2', paddingLeft: '24px', paddingRight: '24px' }}
        >
          <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', backgroundColor: '#FFFFFF' }}>
            Cancel
          </button>
          <button
            type="submit"
            form="edit-shipment-form"
            disabled={submitting}
            className="px-6 py-2 rounded-lg font-medium text-white hover:opacity-90 disabled:opacity-60"
            style={{ fontFamily: 'Lexend Deca', fontSize: '14px', backgroundColor: '#212126' }}
          >
            {submitting ? 'Saving…' : 'SAVE'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default EditShipmentModal
